import { Link } from 'react-router-dom';
import { ShoppingBag, ArrowRight, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CustomerLayout } from '@/components/layout/CustomerLayout';
import { CartItem } from '@/components/CartItem';
import { useCart } from '@/context/CartContext';

export default function CartPage() {
  const { cartItems, cartTotal, cartCount, clearCart } = useCart();
  const deliveryFee = cartTotal >= 199 ? 0 : 30;
  const grandTotal = cartTotal + deliveryFee;

  if (cartItems.length === 0) {
    return (
      <CustomerLayout>
        <div className="px-4 py-12 max-w-md mx-auto text-center" data-testid="empty-cart">
          <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-12 h-12 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-bold mb-2">Your cart is empty</h2>
          <p className="text-muted-foreground mb-6">
            Looks like you haven't added anything to your cart yet.
          </p>
          <Link to="/products" data-testid="start-shopping-btn">
            <Button className="rounded-full h-12 px-8">
              Start Shopping
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      <div className="px-4 py-4 max-w-3xl mx-auto" data-testid="cart-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl md:text-2xl font-bold">Your Cart ({cartCount})</h1>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-destructive hover:text-destructive/80"
            onClick={clearCart}
            data-testid="clear-cart-btn"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Clear
          </Button>
        </div>

        {/* Cart Items */}
        <div className="space-y-3 mb-6">
          {cartItems.map((item) => (
            <CartItem key={item.product_id} item={item} />
          ))}
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-xl border border-border p-4 mb-6" data-testid="order-summary">
          <h3 className="font-semibold mb-4">Order Summary</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span>₹{cartTotal.toFixed(0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Delivery Fee</span>
              <span className={deliveryFee === 0 ? 'text-primary' : ''}>
                {deliveryFee === 0 ? 'FREE' : `₹${deliveryFee}`}
              </span>
            </div>
            {cartTotal < 199 && (
              <p className="text-xs text-muted-foreground bg-muted p-2 rounded">
                Add ₹{(199 - cartTotal).toFixed(0)} more for free delivery
              </p>
            )}
            <div className="border-t border-border pt-2 mt-2 flex justify-between font-bold text-base">
              <span>Total</span>
              <span className="text-primary">₹{grandTotal.toFixed(0)}</span>
            </div>
          </div>
        </div>

        {/* Checkout Button - Fixed on Mobile */}
        <div className="sticky bottom-20 md:bottom-4 bg-background py-3 -mx-4 px-4 border-t border-border md:border-none md:bg-transparent">
          <Link to="/checkout" className="block" data-testid="checkout-btn">
            <Button className="w-full h-14 rounded-full text-base font-semibold">
              Proceed to Checkout
              <span className="ml-2 bg-white/20 px-3 py-1 rounded-full text-sm">
                ₹{grandTotal.toFixed(0)}
              </span>
            </Button>
          </Link>
        </div>
      </div>
    </CustomerLayout>
  );
}
